import { OfferSessionStorage } from '../../shared/types/Offer';
import { Offer } from '../../types';
import { logInfo } from '../../shared/utils/logging';
import { REGISTERED_APPS } from '../../shared/types/Apps';

const ADDED_OFFERS_KEY = 'candycart-added-offers';

function getCurrentOfferOnMatch(
  alreadyAddedOffers: OfferSessionStorage[],
  offerToAdd: Offer,
) {
  return alreadyAddedOffers.find((addedOffer) => {
    const idsMatch = addedOffer.id === offerToAdd.id;
    if (idsMatch && offerToAdd.shopifyProductVariantIdComputedInFE) {
      return (
        addedOffer.shopify_product_variant_id ===
        offerToAdd.shopifyProductVariantIdComputedInFE
      );
    } else {
      return idsMatch;
    }
  });
}

/*
 * Changes the quantity of the offer if it already exists inside the addedOffers that are stored
 * in the sessionStorage. If it doesn't exist we add it to it.
 */
function saveAddedOffer(offerToAdd: Offer) {
  let alreadyAddedOffers = getAddedOffers();
  const currentOffer = getCurrentOfferOnMatch(alreadyAddedOffers, offerToAdd);

  if (currentOffer) {
    // note that currentOffer is an offer from alreadyAddedOffers.
    currentOffer.quantity = offerToAdd.quantityComputedInFE || 1;
    logInfo(
      `Offer already exists. Updated quantity: ${currentOffer.quantity}`,
      REGISTERED_APPS.CANDYCART,
    );
  } else if (offerToAdd.shopifyProductVariantIdComputedInFE) {
    const newOffer = {
      id: offerToAdd.id,
      shopify_product_variant_id:
        offerToAdd.shopifyProductVariantIdComputedInFE,
      quantity: offerToAdd.quantityComputedInFE || 1,
      note: offerToAdd.noteComputedInFE,
    };
    alreadyAddedOffers = [...alreadyAddedOffers, newOffer];
    logInfo(`Added new offer: ${newOffer}`, REGISTERED_APPS.CANDYCART);
  }
  sessionStorage.setItem(ADDED_OFFERS_KEY, JSON.stringify(alreadyAddedOffers));
}

function cleanupStaleAddedRecommendedProducts(
  offerId: number,
  variantsIds: number[],
) {
  sessionStorage.setItem(
    ADDED_OFFERS_KEY,
    JSON.stringify([
      ...getAddedOffers().filter((o: OfferSessionStorage) => {
        return !(
          o.id === offerId &&
          !variantsIds.includes(o.shopify_product_variant_id)
        );
      }),
    ]),
  );
}

function removeOffer(offer: Offer | OfferSessionStorage, variantId?: number) {
  sessionStorage.setItem(
    ADDED_OFFERS_KEY,
    JSON.stringify([
      ...getAddedOffers().filter(
        (o: OfferSessionStorage) =>
          !(offer.id === o.id && o.shopify_product_variant_id === variantId),
      ),
    ]),
  );
}

function getAddedOffers(): OfferSessionStorage[] {
  const data = sessionStorage.getItem(ADDED_OFFERS_KEY);
  if (data) {
    try {
      return JSON.parse(data);
    } catch (error) {
      return [];
    }
  }
  return [];
}

export {
  saveAddedOffer,
  cleanupStaleAddedRecommendedProducts,
  removeOffer,
  getAddedOffers,
};
